import { useEffect, useState, useRef, useCallback } from "react";
import Countdown from "react-countdown";
import { useSelector, useDispatch } from "react-redux";
import "./style.css"; // Buat file CSS untuk styling custom
import CountdownRightImage from "../../assets/images/icons/portal-order/Refreshment_Homepage_Icon_2025_Server.webp";
import { useLocation } from "react-router-dom";
import { notifyWarning } from "components/ToastMessage";

const COUNTDOWN_LABELS = [
  "Promo Hampir Habis!",
  "Waktu Terbatas!",
  "Amankan Promo!",
  "Promo Bakal Hilang Dalam!",
];

const PROMO_MESSAGES_1 = [
  "Jangan tunggu lagi! ",
  "Klik sekarang,",
  "Amankan diskon eksklusifmu sekarang juga, ",
  "Website impian tinggal selangkah lagi,",
];

const PROMO_MESSAGES_2 = [
  "Langsung amankan paket pilihanmu sebelum hilang.",
  "Aktifkan hostingmu hari ini juga!",
  "Sebelum waktu habis!",
  "Aktifkan sekarang!",
];

function getRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

const getCountdownEndTime = () => {
  return parseInt(localStorage.getItem('countdownEndTime'), 10) || (Date.now() + 30 * 60 * 1000);
};

const getProductSignature = (productsList) => {
  if (!productsList || productsList.length === 0) return '';
  // Create a signature based on product IDs and quantities
  return productsList
    .map(product => `${product.pid}-${product.quantity || 1}`)
    .sort()
    .join('|');
};

const showWarningNotification = (productsList, dispatch) => {
  if (!productsList || productsList.length === 0) return;
  
  // Get the first product name for notification
  const productName = productsList[0]?.name || productsList[0]?.product_name || 'Produk';
  
  // Create notification message
  const message = `Penawaran Terbatas! ${productName} masih di keranjang, Cepat selesaikan pesananmu sekarang!`;
  
  // Use the existing notifyWarning from ToastMessage
  notifyWarning(message);
};

const usePersistentCountdown = (productsList) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const [endTime, setEndTime] = useState(getCountdownEndTime());
  const lastProductSignature = useRef('');
  const isInitialized = useRef(false);
  const hasProductsChanged = useRef(false);
  const hasShownWarning = useRef(false);
  const lastLocation = useRef(location.pathname);

  const resetCountdown = useCallback(() => {
    const newEndTime = Date.now() + 30 * 60 * 1000;
    localStorage.setItem('countdownEndTime', newEndTime.toString());
    setEndTime(newEndTime);
    hasShownWarning.current = false; // Reset warning flag
  }, []);

  const handleCountdownComplete = useCallback(() => {
    if (!hasShownWarning.current && productsList && productsList.length > 0) {
      showWarningNotification(productsList, dispatch);
      hasShownWarning.current = true;
    }
    // Reset countdown to 30 minutes after a short delay
    setTimeout(() => {
      resetCountdown();
    }, 2000); // 2 second delay to show the notification
  }, [productsList, resetCountdown, dispatch]);

  useEffect(() => {
    const isOrderPage = [
      "/orderdomain", "/orderhosting", "/orderserver", "/orderproduct", 
      "/orderdomainbackorder", "/migrasihosting"
    ].includes(location.pathname);
    
    // Always use existing countdown, don't reset on page changes
    setEndTime(getCountdownEndTime());
    
    // Only reset if moving from checkout back to order pages (user went back)
    if (isOrderPage && lastLocation.current && 
        ["/paymentmethod", "/ordersummary"].includes(lastLocation.current)) {
      // User went back from checkout, don't reset countdown
    }
    
    lastLocation.current = location.pathname;
    
    if (!isInitialized.current) {
      isInitialized.current = true;
    }
  }, [location.pathname]);

  useEffect(() => {
    const isCheckout = ["/paymentmethod", "/ordersummary"].includes(location.pathname);
    
    if (!isCheckout && productsList) {
      const currentSignature = getProductSignature(productsList);
      
      // Only reset countdown if products actually changed significantly
      // (different product types, not just quantity changes)
      if (currentSignature !== lastProductSignature.current && 
          lastProductSignature.current !== '' && 
          !hasProductsChanged.current) {
        
        // Check if the change is significant (different product types)
        const currentProducts = currentSignature.split('|').map(item => item.split('-')[0]);
        const prevProducts = lastProductSignature.current.split('|').map(item => item.split('-')[0]);
        
        const isSignificantChange = 
          currentProducts.length !== prevProducts.length ||
          currentProducts.some((product, index) => product !== prevProducts[index]);
        
        if (isSignificantChange) {
          resetCountdown();
          hasProductsChanged.current = true;
        }
      }
      
      // Reset the flag when signature stabilizes
      if (currentSignature === lastProductSignature.current) {
        hasProductsChanged.current = false;
      }
      
      lastProductSignature.current = currentSignature;
    }
  }, [productsList, resetCountdown, location.pathname]);

  return { endTime, handleCountdownComplete };
};

const CountdownBanner = () => {
  const { productsList } = useSelector((state) => state.cart);
  const { endTime, handleCountdownComplete } = usePersistentCountdown(productsList);
  const [label, setLabel] = useState(getRandom(COUNTDOWN_LABELS));
  const [isExpired, setIsExpired] = useState(false);
  const randomIndex = Math.floor(Math.random() * PROMO_MESSAGES_1.length);
  const [promoMsg] = useState(PROMO_MESSAGES_1[randomIndex]);
  const [promoMsg2] = useState(PROMO_MESSAGES_2[randomIndex]);
  
  // State untuk responsive
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth <= 900;
  const isSmallMobile = windowWidth <= 480;
  const isVerySmallMobile = windowWidth <= 320;

  // Request notification permission on component mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update label when productsList changes or window size changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isMobile) {
        setLabel("Promo! Waktu Terbatas!");
      } else {
        setLabel(getRandom(COUNTDOWN_LABELS));
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [productsList?.length, isMobile, windowWidth]); // Added windowWidth dependency

  // Check if countdown is completed using useEffect
  useEffect(() => {
    const checkCompletion = () => {
      const now = Date.now();
      const isCompleted = now >= endTime;
      setIsExpired(isCompleted);
    };

    // Check immediately
    checkCompletion();

    // Set up interval to check periodically
    const interval = setInterval(checkCompletion, 1000);

    return () => clearInterval(interval);
  }, [endTime]);

  // Custom renderer untuk format 00 : 00 : 00 (tanpa hari)
  const renderer = ({ hours, minutes, seconds, completed }) => {
    const pad = (n) => String(n).padStart(2, "0");
    const boxStyle = {
      background: "#f2f2f2",
      color: "#000",
      borderRadius: isVerySmallMobile ? 3 : (isSmallMobile ? 4 : (isMobile ? 6 : 8)),
      padding: isVerySmallMobile ? "2px 3px" : (isSmallMobile ? "3px 4px" : (isMobile ? "4px 6px" : "6px 10px")),
      fontWeight: 700,
      fontSize: isVerySmallMobile ? 12 : (isSmallMobile ? 14 : (isMobile ? 16 : 22)),
      display: "inline-block",
      minWidth: isVerySmallMobile ? 12 : (isSmallMobile ? 14 : (isMobile ? 16 : 20)),
      textAlign: "center",
      margin: "0 1px",
      boxShadow: "0 1px 2px rgba(0,0,0,0.1)"
    };
    const renderDigits = (val) => (
      <>
        <span style={boxStyle}>{val[0]}</span>
        <span style={boxStyle}>{val[1]}</span>
      </>
    );
    
    if (completed) {
      return (
        <>
          {renderDigits("00")}
          <span style={{ fontWeight: 700, color: "#000", margin: "0 2px", fontSize: isVerySmallMobile ? 12 : (isSmallMobile ? 14 : (isMobile ? 16 : 22)) }}> : </span>
          {renderDigits("00")}
          <span style={{ fontWeight: 700, color: "#000", margin: "0 2px", fontSize: isVerySmallMobile ? 12 : (isSmallMobile ? 14 : (isMobile ? 16 : 22)) }}> : </span>
          {renderDigits("00")}
        </>
      );
    } else {
      const h = pad(hours);
      const m = pad(minutes);
      const s = pad(seconds);
      return (
        <>
          {renderDigits(h)}
          <span style={{ fontWeight: 700, color: "#000", margin: "0 2px", fontSize: isVerySmallMobile ? 12 : (isSmallMobile ? 14 : (isMobile ? 16 : 22)) }}> : </span>
          {renderDigits(m)}
          <span style={{ fontWeight: 700, color: "#000", margin: "0 2px", fontSize: isVerySmallMobile ? 12 : (isSmallMobile ? 14 : (isMobile ? 16 : 22)) }}> : </span>
          {renderDigits(s)}
        </>
      );
    }
  };

  return (
    <div className="countdown-banner d-flex align-items-center justify-content-between" style={{marginBottom: isVerySmallMobile ? '8px' : (isSmallMobile ? '12px' : (isMobile ? '16px' : '24px'))}}>
      {/* Countdown kiri */}
      <div className={`countdown-timer d-flex flex-column align-items-center justify-content-center ${isExpired ? 'expired' : ''}`}>
        <div className="small mb-1 fw-bold" style={{
          fontSize: isVerySmallMobile ? 10 : (isSmallMobile ? 12 : (isMobile ? 14 : 20)), 
          color: "#000000", 
          marginBottom: isVerySmallMobile ? '3px' : (isSmallMobile ? '4px' : '5px'),
          textAlign: 'center'
        }}>{label}</div>
        <div className={`timer fw-bold ${isExpired ? 'expired' : ''}`}>
          <Countdown 
            key={endTime} 
            date={endTime} 
            renderer={renderer}
            onComplete={handleCountdownComplete}
          />
        </div>
        <div className="timer-labels d-flex justify-content-center" style={{
          gap: isVerySmallMobile ? 2 : (isSmallMobile ? 4 : (isMobile ? 6 : 8)), 
          marginTop: isVerySmallMobile ? '3px' : (isSmallMobile ? '4px' : '5px')
        }}>
          <div style={{
            minWidth: isVerySmallMobile ? 16 : (isSmallMobile ? 18 : (isMobile ? 20 : 44)), 
            textAlign: 'center', 
            fontSize: isVerySmallMobile ? 8 : (isSmallMobile ? 9 : (isMobile ? 10 : 16)), 
            color: '#222'
          }}>Jam</div>
          <div style={{
            minWidth: isVerySmallMobile ? 16 : (isSmallMobile ? 18 : (isMobile ? 20 : 44)), 
            textAlign: 'center', 
            fontSize: isVerySmallMobile ? 8 : (isSmallMobile ? 9 : (isMobile ? 10 : 16)), 
            color: '#222'
          }}>Menit</div>
          <div style={{
            minWidth: isVerySmallMobile ? 16 : (isSmallMobile ? 18 : (isMobile ? 20 : 44)), 
            textAlign: 'center', 
            fontSize: isVerySmallMobile ? 8 : (isSmallMobile ? 9 : (isMobile ? 10 : 16)), 
            color: '#222'
          }}>Detik</div>
        </div>
      </div>
      {!isMobile && (
        <>
          {/* Promo tengah */}
          <div className="banner-text fw-bold fs-4 d-flex flex-column align-items-start flex-grow-1">
            <div style={{marginLeft: 20}} className="mb-1">{promoMsg}</div>
            <div style={{marginLeft: 20}} className="product-names mb-1">{promoMsg2}</div>
          </div>
          {/* Icon kanan */}
          <div className="banner-icon ms-4 d-flex align-items-center justify-content-center">
            <img
              src={CountdownRightImage}
              alt="Promo Icon"
              style={{ width: 130, height: 130, objectFit: "contain" }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default CountdownBanner;
